// =====================================================================
// SALÉSOL — Food Book Pages
// =====================================================================
// Each "lot" follows the same coffee schema with three additions:
//   signature: true     — adds the ◆ house-signature mark next to name
//   photo:    'file.jpg' — opens a photo modal on tap.
//                          Drop the JPG into public/menu-photos/food/
//   addons: [{label, price}] — optional sub-items shown in modal
//   archived: 'YYYY-MM-DD'   — soft-delete; renderer skips this lot
//                              but data stays for un-archive
//
// To update a dish:
//   1. Edit the lot (name, price, notes).
//   2. Save.
//   3. git add . && git commit -m "food: <change>" && git push
//   4. Cloudflare rebuilds in ~60s.
//
// To archive (your "removed but not deleted" rule):
//   Add `archived: '2026-MM-DD',` to the lot. To un-archive, delete that line.
//
// To swap a photo:
//   Drop a new JPG at public/menu-photos/food/<filename>.jpg and reference it
//   via the lot's `photo:` field. No code change needed if filename matches.
//
// FOOTER: "All dishes prepared with no added MSG" — auto-rendered on every
//         food page. Don't add it per page.
// =====================================================================

export const foodPages = [
  { slug: '', kind: 'slipcase' },
  { slug: 'cover', kind: 'cover-front' },

  // -------------------------------------------------------------------
  // 1. FRESH PASTA
  // -------------------------------------------------------------------
  {
    slug: 'pasta',
    kind: 'menu',
    runner: 'I. Kitchen',
    eyebrow: 'I. Kitchen',
    headline: { size: 'lg', prefix: 'Fresh ', italic: 'pasta' },
    pagemark: { section: 'KITCHEN' },
    modifierKey: 'foodMsgNote',
    categories: [
      {
        label: 'Fresh Pasta',
        lots: [
          {
            name: 'Aglio e Olio',
            price: '50',
            notes: 'Spaghetti, garlic-infused olive oil, confit garlic, cherry tomatoes.',
            addons: [
              { label: 'Free-range chicken', price: '+35' },
              { label: 'Norwegian salmon', price: '+95' },
            ],
          },
          {
            name: 'Pesto di Casa',
            price: '70',
            signature: true,
            notes: 'Tagliatelle, house basil-almond pesto, parmigiano & pecorino.',
            photo: 'food/pesto-di-casa.jpg',
            addons: [
              { label: 'Free-range chicken', price: '+35' },
              { label: 'Norwegian salmon', price: '+95' },
            ],
          },
          {
            name: 'Beef Carbonara alla Romana',
            price: '95',
            notes: 'Spaghetti, smoked beef, free-range egg yolk, pecorino romano.',
          },
          {
            name: 'Ragu alla Bolognese',
            price: '105',
            signature: true,
            notes: 'Tagliatelle, Wagyu MB5 ragù slow-braised in red wine and tomato.',
            photo: 'food/ragu-bolognese.jpg',
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------
  // 2. FISH + ALL DAY BRUNCH
  // -------------------------------------------------------------------
  {
    slug: 'fish-brunch',
    kind: 'menu',
    runner: 'I. Kitchen',
    eyebrow: 'I. Kitchen',
    headline: { size: 'lg', prefix: 'Fish & ', italic: 'Brunch' },
    pagemark: { section: 'KITCHEN' },
    modifierKey: 'foodMsgNote',
    categories: [
      {
        label: 'Fish',
        lots: [
          {
            name: 'Seared Salmon',
            price: '140',
            notes: 'Norwegian salmon (130–150g), asparagus, house béchamel, rustic potatoes.',
          },
          {
            name: 'Red Curry Salmon',
            price: '140',
            signature: true,
            notes: 'Norwegian salmon, fragrant red curry, eggplant, cherry tomatoes, steamed rice.',
            photo: 'food/red-curry-salmon.jpg',
          },
        ],
      },
      {
        label: 'All Day Brunch',
        lots: [
          {
            name: 'Bircher Muesli',
            price: '55',
            signature: true,
            notes: 'House granola, almond milk, seasonal fruits, coconut yogurt.',
            photo: 'food/bircher-muesli.jpg',
          },
          {
            name: 'Soufflé Pancake',
            price: '60',
            notes: 'Soft, jiggling pancake — freshly made to order. House strawberry jam, granola, vanilla ice cream.',
            photo: 'food/souffle-pancake.jpg',
          },
          {
            name: 'Chilli Scrambled Eggs',
            price: '60',
            notes: 'Scrambled eggs on sourdough, mushroom, house truffle-chili aioli.',
            photo: 'food/chilli-scrambled-eggs.jpg',
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------
  // 3. RICE
  // -------------------------------------------------------------------
  {
    slug: 'rice',
    kind: 'menu',
    runner: 'I. Kitchen',
    eyebrow: 'I. Kitchen',
    headline: { size: 'xl', italic: 'Rice' },
    pagemark: { section: 'KITCHEN' },
    modifierKey: 'foodMsgNote',
    categories: [
      {
        label: 'Rice & Bowls',
        lots: [
          {
            name: 'Nasi Goreng Roa',
            price: '65',
            signature: true,
            notes: 'Fried rice, house sambal roa, soft egg, fried shallots, ito togarashi.',
          },
          {
            name: 'Karaage Bowl',
            price: '65',
            notes: 'Ginger-soy-sake chicken, yuzu aioli, crispy kale, rice.',
          },
          {
            name: 'Tuna Toro Bowl',
            price: '95',
            notes: 'Seared bluefin otoro, steamed rice, pak choy, crispy kale.',
            photo: 'food/tuna-toro-bowl.jpg',
          },
          {
            name: 'Wagyu Beef Rendang',
            price: '105',
            notes: 'Slow-braised Wagyu MB5 in aromatic spices. Steamed rice, fried shallots, prawn crackers.',
            photo: 'food/wagyu-rendang.jpg',
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------
  // 4. BURGER + PIZZA
  // -------------------------------------------------------------------
  {
    slug: 'burger-pizza',
    kind: 'menu',
    runner: 'I. Kitchen',
    eyebrow: 'I. Kitchen',
    headline: { size: 'lg', prefix: 'Burger & ', italic: 'Pizza' },
    pagemark: { section: 'KITCHEN' },
    modifierKey: 'foodMsgNote',
    categories: [
      {
        label: 'Burger',
        lots: [
          {
            name: 'B.A Classic',
            price: '130',
            notes: 'Black Angus (150g), caramelised onions, pickles, house sauce. Triple-cooked fries on the side.',
            photo: 'food/ba-classic.jpg',
            addons: [{ label: 'Fried egg', price: '+10' }],
          },
        ],
      },
      {
        label: 'Neapolitan Pizza',
        lots: [
          {
            name: 'Margherita Classica',
            price: '70',
            notes: 'Sourdough base, San Marzano tomatoes, mozzarella, basil, olive oil.',
            photo: 'food/margherita-pizza.jpg',
          },
          {
            name: 'Pesto e Pollo',
            price: '85',
            notes: 'House pesto, mozzarella, sous-vide chicken, aged parmesan.',
            photo: 'food/pesto-pizza.jpg',
          },
          {
            name: 'Truffle Chicken and Mushroom',
            price: '95',
            signature: true,
            notes: 'House béchamel, mozzarella, chicken, mushrooms, enoki, black truffle pâté and oil.',
            photo: 'food/truffle-chicken-pizza.jpg',
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------
  // 5. BITES
  // -------------------------------------------------------------------
  {
    slug: 'bites',
    kind: 'menu',
    runner: 'I. Kitchen',
    eyebrow: 'I. Kitchen',
    headline: { size: 'xl', italic: 'Bites' },
    pagemark: { section: 'KITCHEN' },
    modifierKey: 'foodMsgNote',
    categories: [
      {
        label: 'Bites',
        lots: [
          {
            name: 'Chicken Wings',
            price: '50',
            notes: 'Dry-rubbed wings, house confit garlic aioli.',
          },
          {
            name: 'Rustic Truffle Fries',
            price: '55',
            notes: 'Twice-cooked potatoes, truffle oil, aged parmesan, truffle-garlic aioli.',
          },
          {
            name: 'Pisang Goreng Sambal Roa',
            price: '55',
            signature: true,
            notes: 'Banana fritters with sambal roa — smoky Manadonese chili and cured fish.',
          },
          {
            name: 'Chicken Karaage',
            price: '55',
            notes: 'Ginger-soy-sake chicken, fried crisp, yuzu aioli.',
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------
  // 6. DESSERT
  // -------------------------------------------------------------------
  {
    slug: 'dessert',
    kind: 'menu',
    runner: 'II. Dessert',
    eyebrow: 'II. Dessert',
    headline: { size: 'lg', prefix: 'Plated ', italic: 'dessert' },
    pagemark: { section: 'DESSERT' },
    categories: [
      {
        label: 'Plated Dessert',
        lots: [
          {
            name: 'Basque Cheesecake',
            price: '55',
            notes: 'Caramelised exterior, soft center. House granola, vanilla ice cream.',
            photo: 'food/basque-cheesecake.jpg',
          },
          {
            // Soft-deleted 2026-05-18 — removed from active menu.
            // Delete the `archived` line to bring it back.
            archived: '2026-05-18',
            name: 'Matcha Basque Cheesecake',
            price: '55',
            notes: 'A Japanese turn on the Basque. House granola, vanilla ice cream.',
          },
          {
            name: 'Soufflé Pancake',
            price: '60',
            notes: 'Soft, jiggling pancake — freshly made to order. House strawberry jam, granola, vanilla ice cream.',
            photo: 'food/souffle-pancake.jpg',
          },
          {
            name: 'Pistachio Tiramisu',
            price: '65',
            signature: true,
            notes: 'Pistachio mascarpone, espresso-soaked ladyfingers, pistachio crumb.',
            photo: 'food/pistachio-tiramisu.jpg',
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------
  // 7. BAKERY + SMOOTHIES
  // -------------------------------------------------------------------
  {
    slug: 'bakery-smoothies',
    kind: 'menu',
    runner: 'III. Bakery',
    eyebrow: 'III. Bakery & Smoothies',
    headline: { size: 'lg', prefix: 'Bakery & ', italic: 'Smoothies' },
    pagemark: { section: 'BAKERY' },
    categories: [
      {
        label: 'From the Bakery',
        lots: [
          { name: 'Plain Croissant', price: '35' },
          { name: 'Almond Croissant', price: '40' },
          { name: 'Pain au Chocolat', price: '40' },
        ],
      },
      {
        label: 'Seasonal Smoothie Blends',
        lots: [
          {
            name: 'Tropical Bliss',
            price: '60',
            notes: 'Pineapple, banana, yuzu, coconut water, chia seeds.',
          },
          {
            name: 'Crimson Root',
            price: '60',
            notes: 'Beetroot, pineapple, cucumber, honey, coconut water, chia seeds.',
          },
          {
            name: 'Blush Bloom',
            price: '60',
            signature: true,
            notes: 'Dragonfruit, banana, greek yogurt, coconut water, chia.',
            photo: 'food/blush-bloom.jpg',
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------
  // 8. DRINKS (tea + iced tea + water — consolidated from old non-coffee book)
  // -------------------------------------------------------------------
  {
    slug: 'drinks',
    kind: 'menu',
    runner: 'IV. Drinks',
    eyebrow: 'IV. Drinks',
    headline: { size: 'lg', prefix: 'Tea & ', italic: 'water' },
    // Scenes menu: short lede under the headline (from the design handoff).
    lede: 'Tea, simply made. Steeped and still.',
    ledeId: 'Tea, dibuat sederhana. Diseduh, didiamkan.',
    pagemark: { section: 'DRINKS' },
    categories: [
      {
        label: 'Tea',
        lots: [
          { name: 'Green Tea', price: '35' },
          { name: 'Earl Grey', price: '35' },
          { name: 'Chamomile', price: '35' },
          { name: 'Peppermint', price: '35' },
        ],
      },
      {
        label: 'Iced Tea',
        lots: [
          { name: 'Peach / Mango', price: '40' },
          { name: 'Lychee', price: '45' },
        ],
      },
      {
        label: 'Mineral Water',
        lots: [
          { name: 'Still', price: '30' },
          { name: 'Sparkling', price: '35' },
        ],
      },
    ],
  },

  { slug: 'back', kind: 'cover-back' },
];

export const foodTheme = {
  bookId: 'food',
  routeBase: '/food',
  coverColor: '#234A3F', // deep forest teal-green — kitchen + non-coffee drinks
  coverColorAccent: '#15302A',
  accentColor: '#E0B97A',
  bookTitle: 'FOOD & DRINKS',
  frontCover: {
    hero: ['Cooked with', 'discipline.', 'Served with', 'warmth.'],
    heroAccentLine: 1, // "discipline." in rust-accent
    tag: 'FOOD & DRINKS',
  },
  backCover: {
    hero: ['Cooked in', 'BSD City.'],
  },
};
